import { useState, useEffect, useRef, useCallback } from 'react';
import SpeechToText from 'speech-to-text';

export const useSpeechToText = (options = {}) => {
  const { onFinalised: externalOnFinalised, onAnythingSaid: externalOnAnythingSaid, language = 'en-US' } = options;

  const [interimText, setInterimText] = useState('');
  const [finalisedText, setFinalisedText] = useState([]);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const [listener, setListener] = useState(null);

  const listeningRef = useRef(listening);
  listeningRef.current = listening;

  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  const onFinalisedRef = useRef(externalOnFinalised);
  onFinalisedRef.current = externalOnFinalised;

  const onAnythingSaidRef = useRef(externalOnAnythingSaid);
  onAnythingSaidRef.current = externalOnAnythingSaid;

  useEffect(() => {
    // Ensure Web Speech API compatibility
    if (typeof window !== 'undefined' && !window.webkitSpeechRecognition && window.SpeechRecognition) {
      window.webkitSpeechRecognition = window.SpeechRecognition;
    }

    const onAnythingSaid = (text) => {
      setInterimText(text);
      if (onAnythingSaidRef.current) {
        onAnythingSaidRef.current(text);
      }
    };

    const onEndEvent = () => {
      if (listeningRef.current && listenerRef.current) {
        try {
          listenerRef.current.startListening();
        } catch (err) {
          console.warn('SpeechToText restart onEndEvent:', err);
        }
      }
    };

    const onFinalised = (text) => {
      setFinalisedText((prev) => [text, ...prev]);
      setInterimText('');
      if (onFinalisedRef.current) {
        onFinalisedRef.current(text);
      }
    };

    try {
      const speechListener = new SpeechToText(onFinalised, onEndEvent, onAnythingSaid, language);
      setListener(speechListener);
      setError(null);
    } catch (err) {
      console.error('SpeechToText initialization error:', err);
      setError(err?.message || 'Speech recognition not supported');
    }

    return () => {
      if (listenerRef.current) {
        try {
          listenerRef.current.stopListening();
        } catch (e) {
          // ignore cleanup errors
        }
      }
    };
  }, [language]);

  const startListening = useCallback(() => {
    if (!listener) return;
    try {
      listeningRef.current = true;
      listener.startListening();
      setListening(true);
    } catch (err) {
      listeningRef.current = false;
      console.error('Error starting speech recognition:', err);
    }
  }, [listener]);

  const stopListening = useCallback(() => {
    if (!listener) return;
    try {
      listeningRef.current = false;
      listener.stopListening();
      setListening(false);
      setInterimText('');
    } catch (err) {
      console.error('Error stopping speech recognition:', err);
    }
  }, [listener]);

  const toggleListening = useCallback(() => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  }, [listening, startListening, stopListening]);

  return {
    interimText,
    finalisedText,
    listening,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
};

export default useSpeechToText;
